import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Layers, MousePointer, Zap, Image, Calculator, PenTool, Clock, Coins, BarChart3, HelpCircle } from 'lucide-react';
import { StatCard } from '../../../shared/StatCard';
import { Badge } from '../../../../components/ui/Badge';
import { type Question, QUESTION_TYPE_REGISTRY } from '../../../../content';
import { containerVariants, itemVariants } from '../../shared/animationVariants';
import { TYPE_CATEGORIES, useRegistryFilters, RegistryToolbar, CategorySection, ExplorerItem } from './registryExplorer';
const getTypeIcon = (type: string) => {
  if (['multiple_choice', 'cijferend_rekenen', 'cijferend_delen', 'number_sequence', 'ratio_table', 'multiplication_grid', 'estimation_slider', 'thermometer_slider'].includes(type)) return Calculator;
  if (['fill_in', 'drag_drop_fill', 'matching', 'ordering', 'text_highlight'].includes(type)) return PenTool;
  if (['clock', 'clock_set', 'schedule_reading'].includes(type)) return Clock;
  if (['money'].includes(type)) return Coins;
  if (['bar_chart', 'line_chart', 'pie_chart', 'table_reading'].includes(type)) return BarChart3;
  return Layers;
};
interface TypesTabProps {
  questions: Question[];
  onSelectType: (type: string) => void;
}
// 'standaard' = MC/fill_in/true_false — usually the BORING ones to look at.
// Admins normally inspect the more interesting types, so collapse by default.
const TYPES_DEFAULT_COLLAPSED = new Set(['standaard']);
export function TypesTab({
  questions,
  onSelectType
}: TypesTabProps) {
  const totalQuestions = questions.length;
  // Build items list
  const allItems = useMemo(() => {
    const itemsMap = new Map<string, ExplorerItem & {
      exampleQuestion: string;
      isInteractive: boolean;
      isCitoEligible: boolean;
      hasBuiltInVisuals: boolean;
    }>();
    Object.entries(QUESTION_TYPE_REGISTRY).forEach(([type, config]) => {
      itemsMap.set(type, {
        id: type,
        label: config.label,
        description: config.description || 'Geen beschrijving',
        icon: getTypeIcon(type),
        usageCount: 0,
        modules: new Set(),
        isRegistered: true,
        exampleQuestion: 'Geen voorbeeld beschikbaar',
        isInteractive: config.isInteractive,
        isCitoEligible: config.isCitoEligible,
        hasBuiltInVisuals: config.hasBuiltInVisuals
      });
    });
    questions.forEach(q => {
      const type = q.questionType;
      if (!type) return;
      if (!itemsMap.has(type)) {
        itemsMap.set(type, {
          id: type,
          label: type,
          description: 'Onbekend vraagtype',
          icon: HelpCircle,
          usageCount: 0,
          modules: new Set(),
          isRegistered: false,
          exampleQuestion: q.question || 'Geen voorbeeld beschikbaar',
          isInteractive: false,
          isCitoEligible: false,
          hasBuiltInVisuals: false
        });
      }
      const item = itemsMap.get(type)!;
      item.usageCount++;
      item.modules.add(q.familyId?.split('-')[0] || 'UNK');
      if (item.exampleQuestion === 'Geen voorbeeld beschikbaar' && q.question) {
        item.exampleQuestion = q.question;
      }
    });
    return Array.from(itemsMap.values()).sort((a, b) => b.usageCount - a.usageCount);
  }, [questions]);
  // Compute default-expanded category IDs:
  // top categories by usage, MINUS the ones we always collapse (e.g. 'standaard')
  const defaultExpandedIds = useMemo(() => {
    const ranked = TYPE_CATEGORIES.map(cat => {
      const total = cat.items.reduce((sum, id) => {
        const it = allItems.find(x => x.id === id);
        return sum + (it?.usageCount ?? 0);
      }, 0);
      return {
        id: cat.id,
        total
      };
    }).filter(c => !TYPES_DEFAULT_COLLAPSED.has(c.id)).sort((a, b) => b.total - a.total);
    return ranked.slice(0, 2).map(c => c.id);
  }, [allItems]);
  const {
    filters,
    setFilters,
    viewMode,
    setViewMode,
    sortMode,
    setSortMode,
    filteredItems,
    availableModules,
    hasActiveFilters,
    clearFilters,
    toggleCategory,
    expandAll,
    collapseAll,
    isCategoryExpanded
  } = useRegistryFilters(allItems, {
    storageKey: 'admin.types',
    defaultExpandedIds
  });
  const activeTypes = allItems.filter(t => t.usageCount > 0).length;
  const totalTypes = allItems.length;
  const interactiveTypes = allItems.filter(t => t.isInteractive && t.usageCount > 0).length;
  const groupedItems = useMemo(() => {
    const grouped = new Map<string, typeof allItems>();
    const mappedIds = new Set<string>();
    TYPE_CATEGORIES.forEach(cat => {
      const catItems = filteredItems.filter(item => cat.items.includes(item.id));
      grouped.set(cat.id, catItems as typeof allItems);
      catItems.forEach(item => mappedIds.add(item.id));
    });
    const uncategorized = filteredItems.filter(item => !mappedIds.has(item.id) && item.usageCount > 0);
    if (uncategorized.length > 0) {
      grouped.set('uncategorized', uncategorized as typeof allItems);
    }
    const unused = filteredItems.filter(item => item.usageCount === 0);
    if (unused.length > 0) {
      grouped.set('unused', unused as typeof allItems);
    }
    return grouped;
  }, [filteredItems]);
  // All visible category IDs (for expand-all)
  const allVisibleCategoryIds = useMemo(() => {
    const ids: string[] = [];
    TYPE_CATEGORIES.forEach(cat => {
      if ((groupedItems.get(cat.id) ?? []).length > 0) ids.push(cat.id);
    });
    if (groupedItems.has('uncategorized')) ids.push('uncategorized');
    if (groupedItems.has('unused')) ids.push('unused');
    return ids;
  }, [groupedItems]);
  const renderCardContent = (item: ExplorerItem) => {
    const typeItem = item as (typeof allItems)[0];
    return <div className="flex flex-col h-full" data-id="element-5219">
        <div className="bg-gray-50 rounded-lg p-2.5 sm:p-3 mb-2.5 sm:mb-3 border border-gray-100" data-id="element-5220">
          <p className="text-[10px] text-gray-500 font-semibold mb-1 uppercase tracking-wider" data-id="element-5221">
            Voorbeeld
          </p>
          <p className="text-[11px] sm:text-xs text-gray-700 line-clamp-2 italic" data-id="element-5222">
            "{typeItem.exampleQuestion}"
          </p>
        </div>

        <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-2.5 sm:mb-3" data-id="element-5223">
          {typeItem.isInteractive ? <Badge variant="info" size="sm" data-id="element-5224">
              <MousePointer className="w-3 h-3 mr-1" data-id="element-5225" />
              Interactief
            </Badge> : <Badge variant="neutral" size="sm" data-id="element-5226">
              Statisch
            </Badge>}
          {typeItem.isCitoEligible && <Badge variant="success" size="sm" data-id="element-5227">
              <Zap className="w-3 h-3 mr-1" data-id="element-5228" />
              CITO
            </Badge>}
          {typeItem.hasBuiltInVisuals && <Badge variant="warning" size="sm" data-id="element-5229">
              <Image className="w-3 h-3 mr-1" data-id="element-5230" />
              Visuals
            </Badge>}
        </div>

        <div className="flex flex-wrap gap-1 sm:gap-1.5 pt-2 border-t border-gray-100 mt-auto" data-id="element-5231">
          {Array.from(item.modules).sort().slice(0, 4).map(m => <span key={m} className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-gray-50 border-gray-200 text-gray-600" data-id="element-5232">
                {m}
              </span>)}
          {item.modules.size > 4 && <span className="text-[10px] text-gray-400 font-medium px-1.5 py-0.5" data-id="element-5233">
              +{item.modules.size - 4}
            </span>}
        </div>
      </div>;
  };
  return <div className="space-y-4 sm:space-y-6" data-id="element-5234">
      <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-4" data-id="element-5235">
        <motion.div variants={itemVariants} data-id="element-5236">
          <StatCard label="Geregistreerde Typen" value={totalTypes} icon={<Layers className="w-5 h-5" data-id="element-5238" />} color="blue" data-id="element-5237" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5239">
          <StatCard label="Actief in Gebruik" value={activeTypes} icon={<Zap className="w-5 h-5" data-id="element-5241" />} color="emerald" data-id="element-5240" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5242">
          <StatCard label="Totaal Vragen" value={totalQuestions.toLocaleString('nl-NL')} icon={<Calculator className="w-5 h-5" data-id="element-5244" />} color="purple" data-id="element-5243" />
        </motion.div>
        <motion.div variants={itemVariants} data-id="element-5245">
          <StatCard label="Interactieve Typen" value={interactiveTypes} icon={<MousePointer className="w-5 h-5" data-id="element-5247" />} color="amber" data-id="element-5246" />
        </motion.div>
      </motion.div>

      <RegistryToolbar title="Vraagtypen Explorer" icon={<Layers className="w-5 h-5 text-blue-500" data-id="element-5249" />} activeCount={activeTypes} totalCount={totalTypes} filters={filters} onFiltersChange={setFilters} viewMode={viewMode} onViewModeChange={setViewMode} availableModules={availableModules} accentColor="blue" sortMode={sortMode} onSortModeChange={setSortMode} onExpandAll={() => expandAll(allVisibleCategoryIds)} onCollapseAll={collapseAll} hasActiveFilters={hasActiveFilters} onClearFilters={clearFilters} data-id="element-5248" />

      <div className="space-y-4 sm:space-y-6" data-id="element-5250">
        {TYPE_CATEGORIES.map(category => {
        const items = groupedItems.get(category.id) || [];
        if (items.length === 0) return null;
        return <CategorySection key={category.id} category={category} items={items} viewMode={viewMode} onSelectItem={onSelectType} isExpanded={isCategoryExpanded(category.id)} onToggleExpanded={() => toggleCategory(category.id)} accentColor="blue" renderCardContent={renderCardContent} data-id="element-5251" />;
      })}

        {groupedItems.has('uncategorized') && <CategorySection category={{
        id: 'uncategorized',
        label: 'Overige Actieve Typen',
        icon: HelpCircle,
        description: 'Niet ingedeeld in een categorie',
        items: []
      }} items={groupedItems.get('uncategorized')!} viewMode={viewMode} onSelectItem={onSelectType} isExpanded={isCategoryExpanded('uncategorized')} onToggleExpanded={() => toggleCategory('uncategorized')} accentColor="blue" renderCardContent={renderCardContent} data-id="element-5252" />}

        {groupedItems.has('unused') && <CategorySection category={{
        id: 'unused',
        label: 'Ongebruikte Typen',
        icon: Layers,
        description: 'Geregistreerd maar niet in gebruik',
        items: []
      }} items={groupedItems.get('unused')!} viewMode={viewMode} onSelectItem={onSelectType} isExpanded={isCategoryExpanded('unused')} onToggleExpanded={() => toggleCategory('unused')} accentColor="blue" data-id="element-5253" />}

        {filteredItems.length === 0 && <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200" data-id="element-5254">
            Geen vraagtypen gevonden die voldoen aan de filters.
          </div>}
      </div>
    </div>;
}